<template>
  <div class="bg-white rounded-2xl w-full px-4 pt-4 md:pt-5 md:px-5">
    <div class="w-full flex-y-center justify-between mb-2 sm:mb-0">
      <h2
        class="mb-2.5 text-lg md:text-xl leading-130 font-semibold text-dark-100"
      >
        {{ title }}
      </h2>
      <div class="flex-shrink-0" v-if="!data?.length && !loading">
        <CRefreshButton
          @refresh-bots="refreshBots"
          :is-refreshed="isRefreshed"
        />
      </div>
    </div>
    <div
      class="flex items-start flex-col-reverse md:flex-row gap-4"
      v-if="data?.length"
    >
      <Transition name="fade" mode="out-in">
        <div :key="singleLoading" class="w-full">
          <template v-if="singleLoading">
            <div class="space-y-2">
              <CPreloader width="130px" height="20px" loading />
              <CPreloader width="220px" height="24px" loading />
            </div>
          </template>
          <template v-else>
            <div
              class="w-full flex items-start flex-wrap gap-2 sm:gap-4 flex-col sm:flex-row"
            >
              <StatisticsCard
                icon-style="bg-yellow"
                :title="$t('assigned_money')"
                :profit="single?.assigned_money"
              />
              <StatisticsCard
                icon-style="bg-green"
                :title="$t('dashboard_info.closed_trades')"
                :percentage="single?.closed_trades_percentage"
                :profit="single?.profit_closed_coin"
              />
              <StatisticsCard
                icon-style="bg-blue"
                :title="$t('dashboard_info.total_profit')"
                :percentage="single?.total_profit_percentage"
                :profit="single?.unrealized_profit"
              />
            </div>
          </template>
        </div>
      </Transition>
      <div
        class="w-full md:w-auto justify-between sm:justify-end flex-y-center space-x-3"
      >
        <FSelect
          v-if="data?.length"
          :key="tabList"
          :options="tabList"
          v-model="activeTab"
          label-key="title"
          value-key="value"
          class="w-full sm:w-auto"
          :placeholder="$t('choose_bot')"
        />
        <FSelect
          v-if="bots?.length"
          :options="bots"
          v-model="botId"
          label-key="strategy"
          value-key="id"
          :placeholder="$t('choose_bot')"
          class="!w-full sm:!w-auto"
          head-styles="sm:w-max"
        />
        <div class="flex-shrink-0">
          <CRefreshButton
            @refresh-bots="refreshBots"
            :is-refreshed="isRefreshed"
          />
        </div>
      </div>
    </div>
    <CCard class="flex-shrink-0 relative min-h-[300px] md:min-h-[378px]">
      <Transition name="fade" mode="out-in">
        <div :key="loading">
          <div
            v-if="loading"
            class="absolute w-full h-full flex-y-center justify-center z-100"
          >
            <div class="spinner-dot" />
          </div>
          <div v-else>
            <Transition name="fade" mode="out-in">
              <div
                v-if="series?.length"
                class="home-line-chart flex justify-center relative w-full h-[300px] sm:!h-[400px] md:!h-[420px] xl:!h-[430px] 2xl:!h-[440px]"
              >
                <Transition name="fade" mode="out-in">
                  <div
                    :key="isSidebarOpen"
                    class="relative w-full mx-auto !max-h-[578px] mb-5"
                  >
                    <v-chart class="chart" :option="chartOptions" autoresize />
                  </div>
                </Transition>
              </div>
              <CNodata
                v-else
                main-class="min-h-[202px]"
                :subtitle="$t('no_data_subtitle_dashboard')"
                :title="$t('no_data_title_dashboard')"
                class="mt-8 px-6 !py-10"
                :button-text="$t('add_bot')"
                @submit="isUserVerified(checkIsActivePlan, true)"
                custom-button-class="max-w-[170px] text-base"
                image="/images/svg/no-data/no-profit.svg"
                :action="
                  user?.user_bots_count <=
                    user?.plan?.allowed_strategies?.length &&
                  user?.user_bots_count === 0
                "
              />
            </Transition>
          </div>
        </div>
      </Transition>
      <CPlanDialog :show="showModal" @close="showModal = false" />
      <BotCreate
        :show-bot-create="createBot"
        @closeBotCreateModal="createBot = false"
        @created="handleCreateBot"
      />
    </CCard>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch, provide } from "vue";
import { useI18n } from "vue-i18n";
import CCard from "@/components/Card/CardWrapper.vue";
import CNodata from "@/components/Common/CNodata.vue";
import dayjs from "dayjs";
import { formatMoneyDecimal } from "@/utils";
import weekday from "dayjs/plugin/weekday";
import { uzLatnLocaleData } from "@/modules/Profile/type";
import CPlanDialog from "@/components/Common/Dialog/CPlanDialog.vue";
import { useAuthStore } from "@/modules/Auth/stores";
import { isUserVerified } from "@/composables/useUserVerified";
import ApiService from "@/services/ApiService";
import FSelect from "@/components/Form/Select/FSelect.vue";
import { useBotStore } from "@/modules/Bots/stores";
import { useRouter } from "vue-router";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { useDashboardStore } from "@/modules/Dashboard/store";
import StatisticsCard from "@/modules/Dashboard/components/StatisticsCard.vue";
import CPreloader from "@/components/CPreloader.vue";
import { getObjectWithHighestTotalOrders } from "@/utils";
import CRefreshButton from "@/components/Common/CRefreshButton.vue";
import BotCreate from "@/modules/Bots/components/BotCreate.vue";
import { useCommonStore } from "@/modules/Pages/stores";
import { trackCustomEvent } from "@/utils/fbq";

dayjs.extend(localizedFormat);
dayjs.extend(weekday);
dayjs.locale("uz-latn", uzLatnLocaleData);
dayjs.locale("uz-latn");
const { t } = useI18n();
interface Props {
  title: string;
  disabled?: boolean;
  totalProfit: number;
  percentage: number;
  closedTrades: number;
  closedTradesPercentage: number;
  assignedMoney: number;
}

defineProps<Props>();

import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart, BarChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent,
} from "echarts/components";
import VChart, { THEME_KEY } from "vue-echarts";
use([
  CanvasRenderer,
  LineChart,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent,
]);

provide(THEME_KEY, "dark");

const botStore = useBotStore();
const authStore = useAuthStore();
const dashboardStore = useDashboardStore();
const commonStore = useCommonStore();

const router = useRouter();
const createBot = ref(false);

const showModal = ref(false);
const activeTab = ref("daily");
const series = ref();
const isRefreshed = ref(false);
const data = ref([]);
const loading = ref(true);
const singleLoading = ref(false);
const botId = ref(null);
const single = ref();
const weekDays = ref({
  1: t("week_days.monday"),
  2: t("week_days.tuesday"),
  3: t("week_days.wednesday"),
  4: t("week_days.thursday"),
  5: t("week_days.friday"),
  6: t("week_days.saturday"),
  7: t("week_days.sunday"),
});

const isSidebarOpen = computed(() => commonStore.isSidebarOpen);

const chartOptions = computed(() => ({
  backgroundColor: "transparent",
  autoresize: true,
  color: ["#16CC53", "#e0e0e0"],
  tooltip: {
    trigger: "axis",
    axisPointer: {
      type: "cross",
      crossStyle: {
        color: "#999",
      },
    },
    formatter: function (params) {
      let result = "";
      params.forEach((param) => {
        const value =
          param.seriesName === t("absolute_profit")
            ? formatMoneyDecimal(param.value[1], 0) + " USDT"
            : param.value[1];
        result += `${param.marker} ${param.seriesName}: ${value}<br/>`;
      });
      return result;
    },
  },
  grid: {
    left: "0%",
    right: "3%",
    bottom: "0%",
    containLabel: true,
  },
  xAxis: {
    type: "time",
    axisLabel: {
      formatter: (value) =>
        activeTab.value === "weekly"
          ? weekDays.value[dayjs(value).weekday()]
          : dayjs(value).format("d MMM"),
    },
  },
  yAxis: [
    {
      type: "value",
      name: t("absolute_profit"),
      axisLabel: {
        formatter: (value) => formatMoneyDecimal(value, 0),
        align: "right",
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: "#f0f0f5",
          type: "dashed",
        },
      },
    },
    {
      type: "value",
      name: t("trade_count"),
      splitLine: { show: false },
    },
  ],
  series: series.value,
}));

const user = computed(() => authStore.user);

const bots = computed(() => botStore.profitBots);

const refreshBots = () => {
  isRefreshed.value = true;
  ApiService.post("/bot/refresh/")
    .then(async () => {
      await dashboardStore.fetchMainData();
      trackCustomEvent("UserRefreshBalance", {
        event_category: "exchange",
        event_label: "refresh_balance",
        button_text: "Refresh Balance",
      });
    })
    .catch((err: any) => {
      console.log(err);
    })
    .finally(() => {
      isRefreshed.value = false;
    });
};

const handleCreateBot = () => {
  router.push({
    name: "Bots",
  });
};

const checkIsActivePlan = () => {
  const planTitle = user.value?.plan;

  if (planTitle !== null && planTitle !== "") {
    createBot.value = true;
  } else {
    showModal.value = true;
  }
};
const tabList = computed(() => [
  {
    id: 0,
    title: t("month"),
    value: "monthly",
  },
  {
    id: 1,
    title: t("week"),
    value: "weekly",
  },
  {
    id: 2,
    title: t("day"),
    value: "daily",
  },
]);

const getGraph = (val: string) => {
  activeTab.value = val;
  // Common series configuration
  series.value = [
    {
      name: t("absolute_profit"),
      lineStyle: {
        color: "#16CC53",
        width: 2,
      },
      itemStyle: {
        color: "#16CC53",
      },
      data: data.value?.map((info) => ({
        value: [dayjs(info.date).format("YYYY-MM-DD"), info?.abs_profit],
      })),
      type: "line",
    },
    {
      name: t("trade_count"),
      yAxisIndex: 1,
      itemStyle: {
        color: "#e9e9ea",
      },
      data: data.value?.map((info) => ({
        value: [dayjs(info.date).format("YYYY-MM-DD"), info?.trade_count],
      })),
      type: "bar",
    },
  ];
};

function getBotSingle(id: number) {
  singleLoading.value = true;
  ApiService.get(`/bot/${id}/`)
    .then((res) => {
      single.value = res?.data;
    })
    .finally(() => {
      singleLoading.value = false;
    });
}

function getBotProfit() {
  getBotSingle(botId.value);
  loading.value = true;
  ApiService.query(`/bot/${botId.value}/profit/`, {
    params: {
      type: activeTab.value,
    },
  })
    .then(async (res) => {
      data.value = res?.data?.results;
      if (data.value?.length) {
        await getGraph(activeTab.value);
      }
    })
    .finally(() => {
      setTimeout(() => {
        loading.value = false;
      }, 300);
    });
}

onMounted(() => {
  botStore
    .getBots({ has_profit: true })
    .then((res) => {
      if (res && res?.length) {
        botId.value = getObjectWithHighestTotalOrders(res)?.id;
        getBotProfit();
      }
    })
    .finally(() => {
      loading.value = false;
    });
});
watch(
  () => botId.value,
  () => {
    getBotProfit();
  },
);
watch(
  () => activeTab.value,
  () => {
    getBotProfit();
    getGraph(activeTab.value);
  },
);
</script>

<style>
.chart-container {
  width: 100%;
  height: 400px;
}
@media screen and (min-width: 1024px) and (max-width: 1124px) {
  .home-line-chart {
    height: 380px !important;
  }
}
.spinner-dot {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background:
    radial-gradient(farthest-side, #16cc53 94%, #0000) top/6.4px 6.4px no-repeat,
    conic-gradient(#0000 30%, #16cc53);
  -webkit-mask: radial-gradient(
    farthest-side,
    #0000 calc(100% - 6.4px),
    #000 0
  );
  animation: spinner-c7wet2 1s infinite linear;
}

@keyframes spinner-c7wet2 {
  100% {
    transform: rotate(1turn);
  }
}
</style>
<style>
.refresh {
  animation: spinIt 0.8s infinite linear !important;
}
@keyframes spinIt {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
.chart {
  height: 100%;
  width: 100%;
}
</style>
